import React, { useState, useEffect } from 'react';
import { Send, MessageCircle, CheckCircle, XCircle, RefreshCw, Phone, Clock, FlaskConical, Wifi, WifiOff, Trash2, Download, Upload } from 'lucide-react';

const WhatsAppTestDashboard = () => {
  const [testMode, setTestMode] = useState(true);
  const [config, setConfig] = useState({
    workerUrl: '',
    accessToken: '',
    phoneNumberId: '',
    recipientNumber: '+15550001234' // Default test number
  });
  
  const [testMessage, setTestMessage] = useState('');
  interface Message {
    id: number | string;
    text: string;
    from: string;
    type: string;
    timestamp: string;
    status?: string;
  }
  
  const [messages, setMessages] = useState<Message[]>([]);
  const [status, setStatus] = useState({ connected: false, checking: false });
  const [sending, setSending] = useState(false);
  const [mockMessages, setMockMessages] = useState<any[]>([]);

  const getApiBase = () => {
    // Use environment variable with correct fallback
    const apiUrl = import.meta.env.VITE_API_URL || 'https://biig-api.b-9f2.workers.dev';
    console.log('API URL:', apiUrl, 'Test Mode:', testMode);
    return testMode ? `${apiUrl}/api/whatsapp-mock` : `${apiUrl}/api/whatsapp`;
  };

  const checkConnection = async () => {
    setStatus({ connected: false, checking: true });
    
    try {
      const url = `${getApiBase()}/status`;
      console.log('Checking connection to:', url);
      
      const response = await fetch(url);
      console.log('Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Response data:', data);
        setStatus({ 
          connected: testMode ? data.mode === 'mock' : data.connected, 
          checking: false 
        });
      } else {
        console.error('Response not OK:', response.status, response.statusText);
        setStatus({ connected: false, checking: false });
      }
    } catch (error) {
      console.error('Connection check failed:', error);
      setStatus({ connected: false, checking: false });
    }
  };

  const loadMockMessages = async () => {
    if (!testMode) return;
    
    try {
      const response = await fetch(`${getApiBase()}/messages`);
      if (response.ok) {
        const data = await response.json();
        setMockMessages(data.messages || []);
        
        // Convert mock messages to display format
        const displayMessages = data.messages.map((msg: any) => ({
          id: msg.id || Date.now() + Math.random(),
          text: msg.message || msg.text?.body || msg.messaging_product,
          from: msg.type === 'sent' ? 'You' : msg.from || 'Test User',
          type: msg.type || 'received',
          timestamp: new Date(msg.timestamp).toLocaleTimeString(),
          status: msg.status
        }));
        
        setMessages(displayMessages.reverse());
      }
    } catch (error) {
      console.error('Failed to load mock messages:', error);
    }
  };

  const clearMockMessages = async () => {
    if (!testMode) return;
    
    try {
      const response = await fetch(`${getApiBase()}/messages`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        setMessages([]);
        setMockMessages([]);
      }
    } catch (error) {
      console.error('Failed to clear messages:', error);
    }
  };

  const simulateIncomingMessage = async () => {
    if (!testMode) return;
    
    const testMessages = [
      "Hello, I need help with my order",
      "What are your business hours?",
      "Can you check my account status?",
      "I'd like to make a reservation",
      "Tell me about your services"
    ];
    
    const randomMessage = testMessages[Math.floor(Math.random() * testMessages.length)];
    
    try {
      const response = await fetch(`${getApiBase()}/simulate-incoming`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          from: '+15559991234',
          message: randomMessage
        })
      });
      
      if (response.ok) {
        // Reload messages after simulation
        setTimeout(loadMockMessages, 500);
      }
    } catch (error) {
      console.error('Failed to simulate message:', error);
    }
  };

  const sendTestMessage = async () => {
    if (!testMessage || !config.recipientNumber) return;
    
    setSending(true);
    
    try {
      const response = await fetch(`${getApiBase()}/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to: config.recipientNumber,
          message: testMessage,
          type: 'text'
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Add message to display
        const newMessage: Message = {
          id: data.messages?.[0]?.id || Date.now(),
          text: testMessage,
          from: 'You',
          type: 'sent',
          timestamp: new Date().toLocaleTimeString(),
          status: 'sent'
        };
        
        setMessages(prev => [newMessage, ...prev]);
        setTestMessage('');
        
        // In test mode, reload messages to see the mock response
        if (testMode) {
          setTimeout(loadMockMessages, 1500);
        }
      } else {
        console.error('Failed to send message');
      }
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setSending(false);
    }
  };

  const verifyWebhook = async () => {
    if (!testMode) return;
    
    try {
      const response = await fetch(`${getApiBase()}/webhook?hub.mode=subscribe&hub.verify_token=test-token&hub.challenge=test123`);
      
      if (response.ok) {
        const text = await response.text();
        alert(`Webhook verification successful! Challenge: ${text}`);
      } else {
        alert('Webhook verification failed');
      }
    } catch (error) {
      console.error('Webhook verification failed:', error);
      alert('Webhook verification error');
    }
  };

  useEffect(() => {
    checkConnection();
    if (testMode) {
      loadMockMessages();
      // Poll for new messages in test mode
      const interval = setInterval(loadMockMessages, 5000);
      return () => clearInterval(interval);
    }
  }, [testMode]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header with Test Mode Toggle */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-500 rounded-xl">
                <MessageCircle className="text-white" size={28} />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">WhatsApp Bot Dashboard</h1>
                <p className="text-gray-500">
                  {testMode ? 'Test Mode - Using Mock API' : 'Production Mode'}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              {/* Test Mode Toggle */}
              <div className="flex items-center gap-2 bg-gray-100 rounded-lg px-3 py-2">
                <FlaskConical size={18} className={testMode ? 'text-purple-600' : 'text-gray-400'} />
                <span className="text-sm font-medium">Test Mode</span>
                <button
                  onClick={() => setTestMode(!testMode)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    testMode ? 'bg-purple-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      testMode ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              
              {/* Connection Status */}
              <div className="flex items-center gap-2">
                {status.checking ? (
                  <RefreshCw className="animate-spin text-gray-400" size={20} />
                ) : status.connected ? (
                  testMode ? (
                    <FlaskConical className="text-purple-600" size={20} />
                  ) : (
                    <CheckCircle className="text-green-500" size={20} />
                  )
                ) : (
                  <XCircle className="text-red-500" size={20} />
                )}
                <span className="text-sm font-medium">
                  {status.checking ? 'Checking...' : 
                   status.connected ? (testMode ? 'Mock API Ready' : 'Connected') : 
                   'Disconnected'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Test Mode Banner */}
        {testMode && (
          <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <FlaskConical className="text-purple-600 mt-1" size={20} />
              <div className="flex-1">
                <h3 className="font-semibold text-purple-900">Test Mode Active</h3>
                <p className="text-sm text-purple-700 mt-1">
                  You're using mock WhatsApp APIs. Perfect for development and testing without a WhatsApp Business account.
                </p>
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={verifyWebhook}
                    className="px-3 py-1 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700"
                  >
                    Test Webhook Verify
                  </button>
                  <button
                    onClick={simulateIncomingMessage}
                    className="px-3 py-1 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700"
                  >
                    Simulate Incoming
                  </button>
                  <button
                    onClick={clearMockMessages}
                    className="px-3 py-1 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700"
                  >
                    Clear History
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Configuration Panel */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Phone size={20} />
              Configuration
            </h2>
            
            <div className="space-y-4">
              {!testMode && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Worker URL
                    </label>
                    <input
                      type="text"
                      placeholder="https://your-bot.workers.dev"
                      value={config.workerUrl}
                      onChange={(e) => setConfig({...config, workerUrl: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Access Token
                    </label>
                    <input
                      type="password"
                      placeholder="Your WhatsApp API token"
                      value={config.accessToken}
                      onChange={(e) => setConfig({...config, accessToken: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number ID
                    </label>
                    <input
                      type="text"
                      placeholder="1234567890123456"
                      value={config.phoneNumberId}
                      onChange={(e) => setConfig({...config, phoneNumberId: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                </>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {testMode ? 'Test Recipient Number' : 'Recipient Number'}
                </label>
                <input
                  type="text"
                  placeholder="+1234567890"
                  value={config.recipientNumber}
                  onChange={(e) => setConfig({...config, recipientNumber: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
                {testMode && (
                  <p className="text-xs text-gray-500 mt-1">
                    Any number format works in test mode
                  </p>
                )}
              </div>
              
              <button
                onClick={checkConnection}
                className="w-full py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-medium"
              >
                Test Connection
              </button>
              
              {testMode && (
                <div className="bg-purple-50 rounded-lg p-3">
                  <p className="text-xs text-purple-700">
                    <strong>Test Mode Features:</strong><br/>
                    • No WhatsApp Business account needed<br/>
                    • Mock webhook responses<br/>
                    • Simulated message delivery<br/>
                    • Message history tracking
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Message Testing Panel */}
          <div className="bg-white rounded-2xl shadow-lg p-6 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <MessageCircle size={20} />
                Test Messages
              </h2>
              {testMode && messages.length > 0 && (
                <span className="text-xs text-gray-500">
                  {messages.length} messages in history
                </span>
              )}
            </div>
            
            {/* Message History */}
            <div className="flex-1 bg-gray-50 rounded-lg p-4 mb-4 max-h-96 overflow-y-auto">
              {messages.length === 0 ? (
                <div className="text-center text-gray-400 py-8">
                  <MessageCircle size={48} className="mx-auto mb-2 opacity-30" />
                  <p>No messages yet</p>
                  <p className="text-sm mt-1">
                    {testMode ? 'Send a test message or simulate incoming' : 'Send a test message to get started'}
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {messages.map(msg => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.type === 'sent' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-xs px-4 py-2 rounded-2xl ${
                          msg.type === 'sent' 
                            ? 'bg-green-500 text-white' 
                            : msg.from === 'System' 
                            ? 'bg-blue-100 text-blue-900'
                            : 'bg-white border border-gray-200'
                        }`}
                      >
                        <p className="text-sm">{msg.text}</p>
                        <div className={`text-xs mt-1 flex items-center gap-1 ${
                          msg.type === 'sent' ? 'text-green-100' : 
                          msg.from === 'System' ? 'text-blue-600' : 'text-gray-400'
                        }`}>
                          <Clock size={10} />
                          {msg.timestamp}
                          {msg.status && (
                            <span className="ml-1">• {msg.status}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Message Input */}
            <div className="flex gap-2">
              <input
                type="text"
                placeholder={testMode ? "Type a test message (mock mode)..." : "Type a test message..."}
                value={testMessage}
                onChange={(e) => setTestMessage(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && sendTestMessage()}
                disabled={!status.connected}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-100"
              />
              <button
                onClick={sendTestMessage}
                disabled={!status.connected || sending || !testMessage}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {sending ? (
                  <RefreshCw className="animate-spin" size={20} />
                ) : (
                  <Send size={20} />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Info Cards */}
        <div className="grid md:grid-cols-3 gap-4 mt-6">
          <div className="bg-white rounded-xl shadow-md p-4">
            <h3 className="font-semibold text-gray-700 mb-2">
              {testMode ? '🧪 Test Mode Guide' : '🚀 Quick Start'}
            </h3>
            <p className="text-sm text-gray-600">
              {testMode ? (
                <>
                  1. No setup required<br/>
                  2. Click "Simulate Incoming"<br/>
                  3. Send test messages<br/>
                  4. View mock responses
                </>
              ) : (
                <>
                  1. Deploy your Worker<br/>
                  2. Add credentials<br/>
                  3. Test connection<br/>
                  4. Send test messages
                </>
              )}
            </p>
          </div>
          
          <div className="bg-white rounded-xl shadow-md p-4">
            <h3 className="font-semibold text-gray-700 mb-2">📱 Message Types</h3>
            <p className="text-sm text-gray-600">
              • Text messages<br/>
              • Images & documents<br/>
              • Interactive buttons<br/>
              • Template messages
            </p>
          </div>
          
          <div className="bg-white rounded-xl shadow-md p-4">
            <h3 className="font-semibold text-gray-700 mb-2">
              {testMode ? '🔬 Test Features' : '⚡ Next Steps'}
            </h3>
            <p className="text-sm text-gray-600">
              {testMode ? (
                <>
                  • Mock webhooks<br/>
                  • Message simulation<br/>
                  • History tracking<br/>
                  • No API limits
                </>
              ) : (
                <>
                  • Add message templates<br/>
                  • Implement media handling<br/>
                  • Create interactive menus<br/>
                  • Add AI responses
                </>
              )}
            </p>
          </div>
        </div>

        {/* API Documentation Link */}
        {testMode && (
          <div className="bg-gray-50 rounded-xl p-4 mt-6">
            <h3 className="font-semibold text-gray-700 mb-2">📚 Mock API Endpoints</h3>
            <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-600">
              <div>
                <strong>Webhook:</strong><br/>
                GET /api/whatsapp-mock/webhook - Verify<br/>
                POST /api/whatsapp-mock/webhook - Receive
              </div>
              <div>
                <strong>Messages:</strong><br/>
                POST /api/whatsapp-mock/send - Send message<br/>
                GET /api/whatsapp-mock/messages - History
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatsAppTestDashboard;